import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ImagePickerDialog from "../ImagePickerDialog";
import HtmlEditor from "./HtmlEditor";

const normalizePairs = (pairs) =>
    Array.isArray(pairs)
        ? pairs.map((pair, idx) => ({
            id: String(pair?.id || `pair${idx + 1}`).trim(),
            left: {
                text: String(pair?.left?.text || ""),
                imgPath: String(pair?.left?.imgPath || ""),
            },
            right: {
                text: String(pair?.right?.text || ""),
                imgPath: String(pair?.right?.imgPath || ""),
            },
        }))
        : [];

const QuestionFormMatchingPairs = ({ value, onChange }) => {
    const v = value || {};
    const pairs = normalizePairs(v.pairs);
    const [helpOpen, setHelpOpen] = useState(false);
    const [pickerTarget, setPickerTarget] = useState(null);

    function emit(patch) {
        onChange({
            ...v,
            type: "matchingPairs",
            pairs,
            ...patch,
        });
    }

    function setPair(idx, side, patch) {
        emit({
            pairs: pairs.map((pair, pairIdx) => (
                pairIdx === idx
                    ? { ...pair, [side]: { ...pair[side], ...patch } }
                    : pair
            )),
        });
    }

    function setPairId(idx, id) {
        emit({
            pairs: pairs.map((pair, pairIdx) => (
                pairIdx === idx ? { ...pair, id } : pair
            )),
        });
    }

    function addPair() {
        emit({
            pairs: [
                ...pairs,
                {
                    id: `pair${pairs.length + 1}`,
                    left: { text: "", imgPath: "" },
                    right: { text: "", imgPath: "" },
                },
            ],
        });
    }

    function removePair(idx) {
        emit({ pairs: pairs.filter((_, pairIdx) => pairIdx !== idx) });
    }

    function pickImage(img) {
        if (!pickerTarget) return;
        const { idx, side } = pickerTarget;
        setPair(idx, side, {
            imgPath: img.url,
            text: pairs[idx]?.[side]?.text || img.originalFilename || "",
        });
        setPickerTarget(null);
    }

    return (
        <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2">Скласти пари</Typography>
                <Tooltip title="Показати інструкцію для налаштування цього завдання">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHelpOpen(true)}
                    >
                        Як користуватися
                    </Button>
                </Tooltip>
            </Stack>

            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => emit({ subtitle: e.target.value })}
                helperText="Необов'язково. Показується під назвою модуля."
                fullWidth
            />

            <HtmlEditor
                label="Питання / title (HTML)"
                value={v.question || ""}
                onChange={(question) => emit({ question })}
                helperText="Заголовок або короткий опис завдання, який побачить учень."
                minHeight={96}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                    label="Назва верхнього набору"
                    value={v.leftTitle || ""}
                    onChange={(e) => emit({ leftTitle: e.target.value })}
                    placeholder="Наприклад: Тваринки"
                    fullWidth
                />
                <TextField
                    label="Назва нижнього набору"
                    value={v.rightTitle || ""}
                    onChange={(e) => emit({ rightTitle: e.target.value })}
                    placeholder="Наприклад: Тіні"
                    fullWidth
                />
            </Stack>

            <TextField
                label="Інструкція (необов'язково)"
                value={v.instruction || ""}
                onChange={(e) => emit({ instruction: e.target.value })}
                helperText="Кастомний текст підказки 'Як грати'. Якщо порожньо, блок не показується."
                fullWidth
                multiline
                minRows={2}
            />

            <TextField
                label="Порада (необов'язково)"
                value={v.tip || ""}
                onChange={(e) => emit({ tip: e.target.value })}
                helperText="Кастомний текст поради внизу завдання. Якщо порожньо, блок не показується."
                fullWidth
                multiline
                minRows={2}
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Пари
            </Typography>

            {pairs.map((pair, idx) => (
                <Box
                    key={`${pair.id}-${idx}`}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2">Пара #{idx + 1}</Typography>
                            <Tooltip title="Видалити пару">
                                <IconButton color="error" onClick={() => removePair(idx)} disabled={pairs.length <= 2}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <TextField
                            label="ID пари"
                            value={pair.id}
                            onChange={(e) => setPairId(idx, e.target.value)}
                            helperText="Службовий код. Має бути унікальним."
                            fullWidth
                        />

                        {["left", "right"].map((side) => (
                            <Box key={side}>
                                <Typography variant="caption" color="text.secondary">
                                    {side === "left" ? "Верхня картка" : "Нижня картка / ціль"}
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 0.5 }}>
                                    <TextField
                                        label="Текст / alt"
                                        value={pair[side].text}
                                        onChange={(e) => setPair(idx, side, { text: e.target.value })}
                                        fullWidth
                                    />
                                    <TextField
                                        label="URL зображення"
                                        value={pair[side].imgPath}
                                        onChange={(e) => setPair(idx, side, { imgPath: e.target.value })}
                                        fullWidth
                                    />
                                    <Button variant="outlined" onClick={() => setPickerTarget({ idx, side })}>
                                        Обрати
                                    </Button>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            ))}

            <Button variant="outlined" onClick={addPair}>
                Додати пару
            </Button>

            <ImagePickerDialog
                open={Boolean(pickerTarget)}
                onClose={() => setPickerTarget(null)}
                onPick={pickImage}
            />

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Як налаштувати завдання "Скласти пари"</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            1. Додайте заголовок або короткий опис у полі "Питання / title".
                        </Typography>
                        <Typography variant="body2">
                            2. Заповніть назви двох наборів. Це можуть бути "Тваринки" і "Тіні", "Слова" і "Переклад", "Автори" і "Твори" тощо.
                        </Typography>
                        <Typography variant="body2">
                            3. Для кожної пари додайте верхню картку і нижню ціль. Картки можуть мати зображення, текст або і те, і те.
                        </Typography>
                        <Typography variant="body2">
                            4. Учень має перенести кожну верхню картку на відповідну нижню ціль. Правильність визначається за ID пари.
                        </Typography>
                        <Typography variant="body2">
                            5. Інструкція і порада необов'язкові та повністю кастомні: якщо поле порожнє, відповідний блок не показується.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default QuestionFormMatchingPairs;
